import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  ShieldCheck,
  Flame,
  Sparkles,
  ArrowRight,
  Brain,
  Eye,
  HeartHandshake,
  UsersRound,
  Infinity as InfinityIcon,
  Sunrise,
  Volume2,
  VolumeX,
  Play,
  Pause,
  Gift,
} from "lucide-react";

/* ===== Curated, verified image set (Unsplash + Pexels) ===== */
const U = (id, w = 1400) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

const IMG = {
  hero:
    "https://images.unsplash.com/photo-1601922046210-41e129a3e64a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1800",
  meditation:
    "https://images.unsplash.com/photo-1561212856-44e9bae482aa?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
  family: "/images/indian-section-workshop.jpg",
  father_daughter: "/images/outdoor-group-demo.jpg",
  phone:
    "https://images.unsplash.com/photo-1603735468910-51890e88eae4?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  gold_tex:
    "https://images.pexels.com/photos/30715673/pexels-photo-30715673.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  // verified Unsplash photo IDs
  eyeCloseup: "/images/moment-before.jpg",
  module1UnderstandingMind: "/images/technique-demo.jpg",
  bonus1Audios: "/images/bonus-1-audios.gif",
  bonus2ScratchCourse: "/images/bonus-2-scratch-course.jpg",
  module2SelfHypnosis: "/images/module-2-self-hypnosis.jpg",
  module3HealingYourself: "/images/module-3-healing-yourself.jpg",
  module4HelpingOthers: "/images/module-4-helping-others.jpg",
  module5GoingDeeper: "/images/module-5-going-deeper.jpg",
  module6MakingItLast: "/images/module-6-making-it-last.jpg",
  indianMeditate: U("1611532736597-de2d4265fba3"),
  brain: U("1559757175-5700dde675bc"),
  healingHands: "/images/technique-demo.jpg",
  lotus: "/images/hands-on-session.jpg",
  twoPeople: U("1573497019940-1c28c88b4f3e"),
  swirl: U("1454179083322-198bb4daae41"),
  mountainCalm: U("1506905925346-21bda4d32df4"),
  yogaSunset: U("1531971589569-0d9370cbe1e5"),
  candleSoft: U("1545239351-1141bd82e8a6"),
  stage: "/images/stage.jpg",
  smoke: U("1493676304819-0d7a8d026dcf"),
  starsNight: U("1474418397713-7ede21d49118"),
  womanCalm: U("1604881991720-f91add269bed"),
  manPortrait: U("1532012197267-da84d127e765"),
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    variants={fadeUp}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-80px" }}
    transition={{ delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const OrangeDivider = ({ className = "" }) => (
  <div className={`flex items-center justify-center gap-4 ${className}`}>
    <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#F89C03]/60" />
    <Flame className="w-3.5 h-3.5 text-[#F89C03] flicker" />
    <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#F89C03]/60" />
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-xs tracking-[0.4em] uppercase text-[#F89C03] font-semibold mb-6">
    {children}
  </p>
);

const Paragraph = ({ children, className = "" }) => (
  <p
    className={`text-base sm:text-lg leading-[1.85] text-[#CFCFCF] font-light ${className}`}
  >
    {children}
  </p>
);

const PullQuote = ({ children }) => (
  <blockquote className="my-12 sm:my-16 pl-6 sm:pl-8 border-l-2 border-[#F89C03]">
    <p className="font-serif-display italic text-2xl sm:text-3xl text-white leading-snug">
      {children}
    </p>
  </blockquote>
);

const ParallaxImage = ({ src, alt, height = "h-[460px]", overlay = "from-black/70 to-transparent", objectPosition = "object-center", children }) => (
  <div className={`relative ${height} overflow-hidden rounded-sm group`}>
    <img
      src={src}
      alt={alt}
      className={`w-full h-full object-cover ${objectPosition} img-zoom`}
      loading="lazy"
    />
    <div
      className={`absolute inset-0 bg-gradient-to-t ${overlay}`}
      aria-hidden="true"
    />
    {children}
  </div>
);

/* ============================ HERO ============================ */
export const Hero = ({ onCta }) => {
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);
  const videoRef = useRef(null);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <section
      data-testid="section-hero"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1005] via-black to-black" />
        <div className="absolute inset-0 candle-glow flicker" />
      </div>

      <div className="relative max-w-5xl mx-auto px-6 lg:px-8 py-32 z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <div className="flex items-center gap-3 mb-8">
            <img
              src="/images/logo.png"
              alt="Hypno Guru"
              className="w-6 h-6 rounded-full object-cover border border-[#F89C03]/40"
            />
            <span className="text-xs tracking-[0.45em] uppercase text-[#F89C03] font-medium">
              Hypnosis · The Course
            </span>
          </div>

          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-white">
            You Have Been Curious About Hypnosis for Years.
            <br />
            <span className="italic shimmer-text">
              Nobody Ever Showed You How to Actually Do It.
            </span>
          </h1>

          <p className="mt-10 max-w-2xl text-lg sm:text-xl text-[#CFCFCF] leading-relaxed font-light">
            Learn everything about hypnosis — heal yourself, help your family —
            for less than the price of one session with a practitioner.
          </p>

          <div
            data-testid="hero-video-wrap"
            className="mt-8 max-w-2xl relative rounded-2xl overflow-hidden border border-[#F89C03]/20 shadow-[0_0_60px_rgba(248,156,3,0.12)]"
          >
            <div className="relative w-full aspect-video bg-black">
              <video
                id="heroVideo"
                ref={videoRef}
                autoPlay
                muted={muted}
                loop
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover"
              >
                <source
                  src="/VIDEOHYPNOSIS.mp4"
                  type="video/mp4"
                />
              </video>
              <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
                <button
                  data-testid="hero-video-play-toggle"
                  onClick={togglePlay}
                  aria-label={playing ? "Pause video" : "Play video"}
                  className="flex items-center justify-center w-11 h-11 rounded-full bg-black/50 text-white backdrop-blur-sm hover:bg-black/70 transition-colors"
                >
                  {playing ? (
                    <Pause className="w-5 h-5" />
                  ) : (
                    <Play className="w-5 h-5 ml-0.5" />
                  )}
                </button>
                <button
                  data-testid="hero-video-mute-toggle"
                  onClick={() => setMuted((m) => !m)}
                  aria-label={muted ? "Unmute video" : "Mute video"}
                  className="flex items-center justify-center w-11 h-11 rounded-full bg-black/50 text-white backdrop-blur-sm hover:bg-black/70 transition-colors"
                >
                  {muted ? (
                    <VolumeX className="w-5 h-5" />
                  ) : (
                    <Volume2 className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <Button
              data-testid="cta-hero"
              onClick={onCta}
              className="h-14 px-8 bg-[#F89C03] hover:bg-[#FFB933] text-black font-semibold tracking-wide rounded-full text-base glow-pulse cta-shine"
            >
              Enroll Now — ₹3,500
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 text-sm text-[#B5B5B5]">
              <ShieldCheck className="w-4 h-4 text-[#F89C03]" />
              60-day money-back guarantee
            </div>
          </div>

          {/* Subtle stats inline (no new copy, just visual numerics derived) */}
          <div className="mt-16 grid grid-cols-3 max-w-xl gap-4 sm:gap-8 border-t border-[#F89C03]/15 pt-8">
            <div>
              <div className="font-serif-display text-2xl sm:text-3xl text-[#F89C03]">20+ hrs</div>
              <div className="text-[9px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.3em] uppercase text-[#9B9B9B] mt-1">
                Of Content
              </div>
            </div>
            <div>
              <div className="font-serif-display text-2xl sm:text-3xl text-[#F89C03]">Bonus</div>
              <div className="text-[9px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.3em] uppercase text-[#9B9B9B] mt-1">
                Worth ₹12000
              </div>
            </div>
            <div>
              <div className="font-serif-display text-2xl sm:text-3xl text-[#F89C03]">71000+</div>
              <div className="text-[9px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.3em] uppercase text-[#9B9B9B] mt-1">
                Students
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.4 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-[#F89C03]"
        >
          <ChevronDown className="w-5 h-5" />
        </motion.div>
      </div>
    </section>
  );
};

/* ============================ LEAD ============================ */
export const Lead = () => (
  <section data-testid="section-lead" className="py-24 sm:py-32 relative">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-5 lg:sticky lg:top-28">
        <Reveal>
          <ParallaxImage
            src={IMG.eyeCloseup}
            alt="A pair of eyes lowering — the moment before trance"
            height="h-[520px]"
            overlay="from-black/60 to-transparent"
          >
            <div className="absolute bottom-5 left-5 right-5">
              <p className="text-[10px] tracking-[0.4em] uppercase text-[#F89C03]">
                The moment just before
              </p>
            </div>
          </ParallaxImage>
        </Reveal>
      </div>

      <div className="lg:col-span-7">
        <Reveal>
          <SectionLabel>An Invitation</SectionLabel>
          <p className="text-2xl sm:text-3xl text-white font-serif-display italic leading-snug">
            You already know hypnosis is real.
          </p>

          <div className="mt-10 space-y-7">
            <Paragraph>
              Maybe you saw it somewhere — a video, a show, a YouTube clip —
              and something in you said:{" "}
              <em className="text-[#FFB933]">
                wait. That is actually doing something.
              </em>
            </Paragraph>
            <Paragraph>
              Maybe someone you know went to a hypnotherapist and came back
              different. Calmer. Lighter. Like something heavy had been put
              down.
            </Paragraph>
            <Paragraph>
              Maybe you have been carrying something for years — an old fear, a
              pattern you cannot shake, a feeling that something is wrong but
              you cannot name exactly what — and you have quietly wondered:
              could that get to it?
            </Paragraph>
          </div>

          <PullQuote>
            You were right.<br />
            It can.
          </PullQuote>

          <Paragraph>
            And the reason you have never learned how to use it yourself is not
            because you are not capable. It is because nobody ever gave you a
            real way in.
          </Paragraph>
          <Paragraph className="mt-6">
            <span className="text-[#FFB933] font-medium">
              This course is that way in.
            </span>
          </Paragraph>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ============================ CERTIFICATE ============================ */
export const CertificateNote = () => (
  <section
    data-testid="section-certificate"
    className="py-20 sm:py-28 relative"
  >
    <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
      <Reveal>
        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
          On successful completion, you will be awarded a{" "}
          <span className="shimmer-text">recognized certificate</span> — a
          mark of your skill and dedication.
        </h2>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-12 mx-auto max-w-2xl relative rounded-2xl overflow-hidden border border-[#F89C03]/30 shadow-[0_0_60px_rgba(248,156,3,0.12)]">
          <img
            src="/images/certificate.jpg"
            alt="A course completion certificate"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

/* ============================ VILLAIN ============================ */
export const Villain = () => (
  <section
    data-testid="section-villain"
    className="py-24 sm:py-32 bg-[#060606] relative"
  >
    <div className="max-w-3xl mx-auto px-6 lg:px-8">
      <Reveal>
        <SectionLabel>Why Nothing Else Worked</SectionLabel>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight mb-12">
          Let us be honest about something.
        </h2>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="space-y-7">
          <Paragraph>You have tried things.</Paragraph>
          <Paragraph>
            Maybe meditation. Maybe pranayama. Maybe yoga. Maybe you went to a
            therapist. Maybe you tried journaling, or affirmations, or that app
            everyone was using. Maybe you went to a Reiki healer, or a sound
            healing session, or a workshop that felt powerful on Saturday and
            faded by Wednesday.
          </Paragraph>
          <Paragraph>
            And maybe you felt something shift — temporarily. And then the old
            thing came back.
          </Paragraph>
          <Paragraph>
            The anxiety. The anger that fires before you can stop it. The
            feeling of not being enough. The fear that lives quietly in the
            background of everything. The pattern in your relationships that
            keeps repeating no matter how much you understand it.
          </Paragraph>
          <Paragraph>You tried. You were sincere. You were consistent.</Paragraph>
        </div>

        <PullQuote>And the core thing did not change.</PullQuote>
      </Reveal>

      <Reveal>
        <Paragraph>Here is why.</Paragraph>
        <Paragraph className="mt-6">
          Every single one of those tools works at the surface of the mind. They
          are good tools. They are not the problem. The problem is that the
          thing you are trying to change is not sitting at the surface.
        </Paragraph>
        <Paragraph className="mt-6 text-[#FFB933] font-serif-display italic text-xl sm:text-2xl">
          It is sitting much deeper.
        </Paragraph>
        <Paragraph className="mt-6">
          And the surface tools — no matter how good they are, no matter how
          sincerely you use them — cannot reach it.
        </Paragraph>
        <Paragraph className="mt-6">This is not your failure.</Paragraph>
        <Paragraph className="mt-3">
          This is a mismatch between the tool and the location of the problem.
        </Paragraph>
      </Reveal>
    </div>

    <div className="mt-24">
      <OrangeDivider />
    </div>
  </section>
);

/* ============================ INDIAN SECTION ============================ */
export const IndianSection = () => (
  <section data-testid="section-indian" className="py-24 sm:py-32 relative">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5 order-2 lg:order-1">
        <Reveal>
          <div className="relative">
            <ParallaxImage
              src={IMG.family}
              alt="An Indian family in quiet conversation"
              height="h-[500px]"
              overlay="from-black/65 to-transparent"
            >
              <div className="absolute bottom-5 left-5 right-5">
                <p className="text-[10px] tracking-[0.35em] uppercase text-[#F89C03]">
                  What we carry. What we pass on.
                </p>
              </div>
            </ParallaxImage>
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-7 order-1 lg:order-2">
        <Reveal>
          <SectionLabel>The Section Most Indians Will Recognise</SectionLabel>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight">
            There is something else.
          </h2>

          <div className="mt-10 space-y-7">
            <Paragraph>
              Most of us grew up in families where you did not talk about what
              was going on inside. You were told to be strong. You were told not
              to make problems. You were told that everyone has difficulties and
              you just have to manage.
            </Paragraph>
            <Paragraph>
              So the fear, the hurt, the pattern — it did not get addressed. It
              got pushed down.
            </Paragraph>
            <Paragraph>
              And pushed-down things do not go away. They go underground. They
              run from below. They show up as the irritation that comes out at
              people you love. As the tension in the body that nobody can
              explain. As the decision you keep making that you know is wrong
              but cannot seem to stop making.
            </Paragraph>
            <Paragraph>
              For generations, nobody in most Indian families had the tools to
              go to that underground level.
            </Paragraph>
            <p className="font-serif-display italic text-2xl sm:text-3xl text-[#FFB933] mt-8">
              Now you do.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ============================ MECHANISM ============================ */
export const Mechanism = () => (
  <section
    data-testid="section-mechanism"
    className="py-24 sm:py-32 bg-[#060606] relative"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-16 items-start">
      <div className="lg:col-span-7">
        <Reveal>
          <SectionLabel>How Hypnosis Actually Works</SectionLabel>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight mb-10">
            Think of your mind like a mobile phone.
          </h2>

          <div className="space-y-7">
            <Paragraph>
              The screen — what you see, what you think, what you are aware of —
              is your conscious mind.
            </Paragraph>
            <Paragraph>
              The operating system running underneath the screen — the software
              that controls everything — is your subconscious mind.
            </Paragraph>
            <Paragraph>
              Now imagine your phone keeps doing something you do not want it to
              do. The screen keeps freezing. An app keeps crashing. The battery
              drains too fast.
            </Paragraph>
            <Paragraph>
              You try to fix it by tapping the screen harder. You download a new
              app. You change the wallpaper.
            </Paragraph>
            <Paragraph className="text-[#FFB933] italic font-serif-display text-xl">
              Nothing works.
            </Paragraph>
            <Paragraph>
              Because the problem is not on the screen. The problem is in the
              operating system. And tapping the screen cannot reach the
              operating system.
            </Paragraph>

            <div className="mt-10 grid sm:grid-cols-3 gap-4 border-y border-[#F89C03]/20 py-8">
              {[
                ["Meditation", "is tapping the screen mindfully."],
                ["Affirmations", "are typing new messages on the screen."],
                ["Yoga", "is cleaning the outside of the phone."],
              ].map(([k, v]) => (
                <div key={k}>
                  <p className="font-serif-display text-2xl text-white">{k}</p>
                  <p className="text-sm text-[#A5A5A5] mt-2 leading-relaxed">
                    {v}
                  </p>
                </div>
              ))}
            </div>

            <Paragraph>All useful. All working at the screen level.</Paragraph>
            <Paragraph>
              None of them going into the operating system.
            </Paragraph>

            <p className="font-serif-display text-3xl sm:text-4xl text-[#FFB933] mt-10 leading-snug">
              Hypnosis goes into the operating system.
            </p>

            <Paragraph className="mt-8">
              When you enter the hypnotic state, the part of your mind that sits
              between your conscious thoughts and your deep subconscious — the
              part that evaluates, judges, defends, and says &ldquo;yes
              but&rdquo; to everything new — goes quiet.
            </Paragraph>
            <Paragraph>For the first time, the subconscious is open.</Paragraph>
            <Paragraph>
              Whatever is running in there — the old belief, the old fear, the
              old pattern written in when you were six years old and did not
              have words for what was happening — can now be found. Read. And
              changed.
            </Paragraph>
            <Paragraph>By you. Not by someone else doing something to you.</Paragraph>
            <Paragraph className="text-[#FFB933] italic font-serif-display text-xl">
              By you, for yourself.
            </Paragraph>
            <Paragraph>
              This is why one real hypnosis session can do what ten years of
              other work could not. Not because it is magic. Because it is the
              correct address.
            </Paragraph>
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-5 lg:sticky lg:top-24">
        <Reveal delay={0.1}>
          <div className="relative rounded-sm border border-[#F89C03]/20 bg-gradient-to-b from-[#0d0d0d] to-black p-10 sm:p-12 min-h-[420px] flex flex-col justify-center overflow-hidden">
            <div className="absolute inset-0 candle-glow opacity-60" />
            <div className="relative flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#F89C03] mb-8">
              <span className="w-2 h-2 rounded-full bg-[#F89C03] animate-pulse" />
              The Operating System
            </div>
            <p className="relative font-serif-display italic text-2xl sm:text-3xl text-white leading-snug">
              The screen is what you see.
              <br />
              The operating system is what runs everything.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ============================ NOT HYPNOSIS ============================ */
export const NotHypnosis = () => (
  <section
    data-testid="section-not-hypnosis"
    className="py-24 sm:py-32 relative"
  >
    <div className="max-w-3xl mx-auto px-6 lg:px-8">
      <Reveal>
        <SectionLabel>What Hypnosis Is Not</SectionLabel>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight mb-10">
          You have seen the stage shows.
        </h2>
      </Reveal>

      <Reveal>
        <div className="my-12 -mx-6 lg:mx-0">
          <ParallaxImage
            src={IMG.stage}
            alt="A stage performance under dim red lights"
            height="h-[360px]"
            overlay="from-black/75 via-black/30 to-black/30"
            objectPosition="object-top"
          />
        </div>
      </Reveal>

      <Reveal>
        <div className="space-y-7">
          <Paragraph>
            Someone swings a watch. A person falls asleep. They wake up and do
            embarrassing things and the audience laughs.
          </Paragraph>
          <Paragraph>
            That is entertainment. It has nothing to do with what you are
            learning here.
          </Paragraph>
          <Paragraph>
            Real hypnosis does not make you unconscious. You are completely
            aware throughout. You can hear everything. You can stop at any time.
            Nobody can make you do anything you do not want to do.
          </Paragraph>
          <Paragraph>
            What happens is much simpler and much more powerful than any of
            that.
          </Paragraph>
          <Paragraph>
            You enter a deeply relaxed state — similar to the feeling just
            before sleep, when the mind is calm and the body is still. In that
            state, the critical, doubting part of the mind relaxes. The deeper
            mind becomes accessible.
          </Paragraph>
          <Paragraph>And in that window, you do the work.</Paragraph>
        </div>

        <PullQuote>
          Most people who experience it for the first time say the same thing: I
          did not expect it to feel so normal. And I did not expect it to work
          so fast.
        </PullQuote>
      </Reveal>
    </div>
  </section>
);

/* ============================ WILL LEARN ============================ */
export const WillLearn = () => (
  <section
    data-testid="section-will-learn"
    className="py-24 sm:py-32 bg-[#060606] relative overflow-hidden"
  >

    <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
      <Reveal>
        <SectionLabel>You Will Learn to Do All of This</SectionLabel>
      </Reveal>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-10">
          <Reveal>
            <div>
              <h3 className="font-serif-display text-2xl sm:text-3xl text-[#FFB933] mb-4">
                Enter the hypnotic state yourself
              </h3>
              <Paragraph>
                Alone, without needing a practitioner. You can do this in five
                minutes, any time you need it, for the rest of your life.
              </Paragraph>
            </div>
          </Reveal>

          <div className="gold-divider" />

          <Reveal delay={0.05}>
            <div>
              <h3 className="font-serif-display text-2xl sm:text-3xl text-[#FFB933] mb-4">
                Work on yourself
              </h3>
              <Paragraph>
                The specific fear, the specific pattern, the specific belief
                that has not moved no matter what you have tried.
              </Paragraph>
            </div>
          </Reveal>

          <div className="gold-divider" />

          <Reveal delay={0.1}>
            <div>
              <h3 className="font-serif-display text-2xl sm:text-3xl text-[#FFB933] mb-4">
                Help your family
              </h3>
              <Paragraph>
                Your mother&rsquo;s lifelong tension. Your father&rsquo;s anger
                that nobody has ever spoken about directly. Your child&rsquo;s
                exam anxiety that is making everyone in the house miserable.
                Your partner&rsquo;s fear that is limiting both of your lives.
              </Paragraph>
            </div>
          </Reveal>

          <div className="gold-divider" />

          <Reveal delay={0.15}>
            <div>
              <Paragraph>
                The person in your life who is suffering the most right now —
                the one you watch struggle and feel helpless because nothing you
                say or do reaches the root of it — you will know how to actually
                help them. Not with advice. Not with support. With access to the
                level where the problem lives.
              </Paragraph>
              <p className="font-serif-display italic text-2xl text-white mt-8">
                This is what the course gives you.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <div className="grid grid-cols-2 gap-4 lg:sticky lg:top-24">
            <Reveal>
              <ParallaxImage
                src={IMG.family}
                alt="A moment from the teaching"
                height="h-56 sm:h-64"
                overlay="from-black/60 to-transparent"
              />
            </Reveal>
            <Reveal delay={0.05}>
              <ParallaxImage
                src={IMG.healingHands}
                alt="Healing hands"
                height="h-56 sm:h-64"
                overlay="from-black/60 to-transparent"
              />
            </Reveal>
            <Reveal delay={0.1}>
              <ParallaxImage
                src={IMG.father_daughter}
                alt="A quiet moment between two people"
                height="h-56 sm:h-64"
                overlay="from-black/60 to-transparent"
              />
            </Reveal>
            <Reveal delay={0.15}>
              <ParallaxImage
                src={IMG.lotus}
                alt="Lotus — the metaphor of inner unfolding"
                height="h-56 sm:h-64"
                overlay="from-black/60 to-transparent"
              />
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ============================ MODULES ============================ */
const modules = [
  {
    n: "01",
    title: "Understanding the Mind",
    body:
      "Why the subconscious runs everything. Why conscious effort alone cannot change subconscious patterns. How the hypnotic state works neurologically. After this module you will understand, clearly and permanently, why everything else you tried worked the way it did — and why this is different.",
    img: IMG.module1UnderstandingMind,
    Icon: Brain,
  },
  {
    n: "02",
    title: "Your First Self-Hypnosis",
    body:
      "Step by step, you will enter the state yourself. Not as a relaxation exercise. The real state. Multiple methods so you find what works for your mind. Most people feel it clearly within the first session.",
    img: IMG.module2SelfHypnosis,
    Icon: Eye,
  },
  {
    n: "03",
    title: "Healing Yourself",
    body:
      "How to go to the specific thing you want to change. How to find its root in the subconscious. How to change it there — not manage it, not cope with it. Change it at the source.",
    img: IMG.module3HealingYourself,
    Icon: HeartHandshake,
  },
  {
    n: "04",
    title: "Helping Others",
    body:
      "How to guide another person into the state. The words to use, the process to follow, the way to work with what comes up. You will be able to do this with a family member within days of finishing this module.",
    img: IMG.module4HelpingOthers,
    Icon: UsersRound,
  },
  {
    n: "05",
    title: "Going Deeper",
    body:
      "Advanced techniques. Rapid inductions. Working with complex patterns. Parts of you that want different things and keep creating internal conflict. Accessing the original memory that started a pattern and revising it.",
    img: IMG.module5GoingDeeper,
    Icon: InfinityIcon,
  },
  {
    n: "06",
    title: "Making It Last",
    body:
      "How to make sure changes hold. How to keep developing the skill. How to build a regular self-hypnosis practice that keeps working for you for years.",
    img: IMG.module6MakingItLast,
    Icon: Sunrise,
  },
];

export const Modules = ({ onCta }) => (
  <section data-testid="section-modules" className="py-24 sm:py-32 relative">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <Reveal>
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <SectionLabel>What Is Inside the Course</SectionLabel>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight">
            Six modules. <span className="shimmer-text">One complete skill.</span>
          </h2>
        </div>
      </Reveal>

      {/* Vertical timeline layout - desktop alternating left/right */}
      <div className="relative">
        <div
          className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#F89C03]/40 to-transparent -translate-x-1/2"
          aria-hidden="true"
        />

        <div className="space-y-16 lg:space-y-28">
          {modules.map((m, i) => {
            const reverse = i % 2 === 1;
            const Icon = m.Icon;
            return (
              <Reveal key={m.n} delay={i * 0.03}>
                <div
                  data-testid={`module-card-${m.n}`}
                  className={`grid lg:grid-cols-12 gap-8 lg:gap-12 items-center relative ${
                    reverse ? "lg:[direction:rtl]" : ""
                  }`}
                >
                  {/* Dot on timeline */}
                  <span className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#F89C03] timeline-dot" />

                  {/* Image */}
                  <div className="lg:col-span-5 [direction:ltr]">
                    <ParallaxImage
                      src={m.img}
                      alt={`Module ${parseInt(m.n)} — ${m.title}`}
                      height="h-72 sm:h-80"
                      overlay="from-black/75 via-black/20 to-transparent"
                    >
                      <div className="absolute top-4 left-4 flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#F89C03] bg-black/40 backdrop-blur-sm px-3 py-1.5 border border-[#F89C03]/30">
                        <Icon className="w-3.5 h-3.5" />
                        Module {parseInt(m.n)}
                      </div>
                      <div className="absolute bottom-4 right-4 font-serif-display text-7xl text-[#F89C03]/85 leading-none">
                        {m.n}
                      </div>
                    </ParallaxImage>
                  </div>

                  {/* Spacer for timeline */}
                  <div className="hidden lg:block lg:col-span-2" />

                  {/* Text */}
                  <div className="lg:col-span-5 [direction:ltr]">
                    <p className="text-xs tracking-[0.35em] uppercase text-[#F89C03] mb-3 font-semibold">
                      Module {parseInt(m.n)}
                    </p>
                    <h3 className="font-serif-display text-3xl sm:text-4xl text-white mb-5 leading-tight">
                      {m.title}
                    </h3>
                    <p className="text-[#CFCFCF] leading-[1.85] font-light">
                      {m.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      <Reveal>
        <div className="text-center mt-20">
          <Button
            data-testid="cta-modules"
            onClick={onCta}
            className="h-13 px-8 py-4 bg-[#F89C03] hover:bg-[#FFB933] text-black font-semibold tracking-wide rounded-full cta-shine"
          >
            Get All 6 Modules — ₹3,500
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ============================ BONUSES ============================ */
const bonuses = [
  {
    n: "01",
    title: "18 Hypnotic Audios",
    body:
      "Ready-made hypnotic audio sessions you can play any time you need them — no need to induce the state from scratch yourself. A shortcut for the days you want the work done for you.",
    img: IMG.bonus1Audios,
  },
  {
    n: "02",
    title: "Instant Hypnosis Scratch Course",
    body:
      "A focused add-on course on inducing hypnosis instantly — the rapid, no-buildup methods used when there is no time for a slow induction.",
    img: IMG.bonus2ScratchCourse,
  },
];

export const Bonuses = () => (
  <section data-testid="section-bonuses" className="pb-24 sm:pb-32 relative">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <Reveal>
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <SectionLabel>Included at No Extra Cost</SectionLabel>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight">
            Plus <span className="shimmer-text">two bonuses.</span>
          </h2>
        </div>
      </Reveal>

      <div className="grid sm:grid-cols-2 gap-8 lg:gap-10">
        {bonuses.map((b, i) => (
          <Reveal key={b.n} delay={i * 0.05}>
            <div
              data-testid={`bonus-card-${b.n}`}
              className="h-full border border-[#F89C03]/25 rounded-sm overflow-hidden bg-black group hover:border-[#F89C03]/50 transition-colors"
            >
              <ParallaxImage
                src={b.img}
                alt={b.title}
                height="h-56 sm:h-64"
                overlay="from-black/80 via-black/10 to-transparent"
              >
                <div className="absolute top-4 left-4 flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#F89C03] bg-black/40 backdrop-blur-sm px-3 py-1.5 border border-[#F89C03]/30">
                  <Gift className="w-3.5 h-3.5" />
                  Bonus {parseInt(b.n)}
                </div>
              </ParallaxImage>
              <div className="p-7 sm:p-8">
                <h3 className="font-serif-display text-2xl sm:text-3xl text-white mb-3 leading-tight">
                  {b.title}
                </h3>
                <p className="text-[#CFCFCF] leading-[1.85] font-light text-[15px]">
                  {b.body}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
export const GalleryStrip = () => {
  const gallery = [
    IMG.eyeCloseup,
    IMG.module1UnderstandingMind,
    IMG.module2SelfHypnosis,
    IMG.healingHands,
    IMG.module3HealingYourself,
    IMG.family,
    IMG.module4HelpingOthers,
    IMG.lotus,
    IMG.module5GoingDeeper,
    IMG.father_daughter,
    IMG.module6MakingItLast,
    IMG.stage,
  ];
  // duplicate for seamless marquee
  const items = [...gallery, ...gallery];
  return (
    <section
      data-testid="section-gallery"
      className="py-16 sm:py-20 bg-black relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-10">
        <Reveal>
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <SectionLabel>Inside the practice</SectionLabel>
              <h3 className="font-serif-display text-3xl sm:text-4xl text-white leading-tight">
                Glimpses of the work.
              </h3>
            </div>
            <OrangeDivider />
          </div>
        </Reveal>
      </div>

      <div className="relative">
        {/* Edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-black to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-black to-transparent z-10" />

        <div className="overflow-hidden">
          <div className="flex gap-4 marquee-track" style={{ width: "max-content" }}>
            {items.map((src, idx) => (
              <div
                key={idx}
                className="relative w-[260px] sm:w-[320px] h-[200px] sm:h-[230px] shrink-0 overflow-hidden rounded-sm border border-[#F89C03]/10"
              >
                <img
                  src={src}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================ TESTIMONIALS ============================ */
const testimonials = [
  {
    body: `I am 34. Since Class 9, I have not spoken voluntarily in any group — not in office meetings, not at family functions, not even at my own cousin's wedding where I was supposed to give a small toast and I made an excuse and left. I built my entire career around avoiding situations where I would have to speak.\n\nOne session. I went back to the exact moment it started — a teacher humiliated me in front of the class when I was twelve. I changed what I believed about that moment.\n\nEight months later I gave a presentation to forty people at my office. I am writing this because I still cannot fully believe it.`,
    name: "Priya S.",
    meta: "34, Pune",
  },
  {
    body: `My son was in Class 10. Board exams. He was not sleeping. He was crying before every practice test. His teacher told me he was one of the brightest students she had but the anxiety was destroying him.\n\nI used what I learned in Module 4. Three sessions with him at home. He went into his board exams calm. He scored in the top 15% of his school.\n\nI cannot describe what it felt like to watch him walk into that exam hall without shaking.`,
    name: "Rajesh M.",
    meta: "47, Hyderabad",
  },
  {
    body: `I have been carrying my mother's sadness my whole life. That is the only way I can describe it. A heaviness that was just always there. I thought it was just who I am.\n\nAfter Module 3 I found where it came from. It was not even mine. It was something she carried that I absorbed as a small child.\n\nI have not felt that heaviness in four months. My mother noticed the change in me before I told her what I had done. She wants to learn it now.`,
    name: "Ananya K.",
    meta: "29, Bengaluru",
  },
  {
    body: `I am a counsellor. Twelve years of practice. I had clients who were completely stuck — same patterns, session after session, genuine effort, no movement.\n\nThis course gave me a tool I was not trained to use. The stuck clients are not stuck anymore.\n\nI refer everyone I know to this course now.`,
    name: "Sunita R.",
    meta: "Clinical Counsellor, Chennai",
  },
];

const videoTestimonials = [
  { id: "1z3awxo24w", caption: "Making everyone blew their mind." },
  { id: "qi046af9ww", caption: "Performing Magic Infront of Friends." },
  { id: "fw3n85fzup", caption: "He turned my reality upside down." },
  { id: "p5yhvf02vx", caption: "He made me doubt everything I know about the world." },
  { id: "taweufrfuk", caption: "Now I feel like I'm living in a parallel universe." },
  { id: "o2pq8s7soh", caption: "It was a 10/10 mind-bender." },
];

export const Testimonials = () => (
  <section
    data-testid="section-testimonials"
    className="py-24 sm:py-32 bg-[#060606] relative"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <Reveal>
        <div className="mb-20 max-w-3xl">
          <SectionLabel>What People Who Have Done This Say</SectionLabel>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight">
            Real voices. <span className="shimmer-text">Real shifts.</span>
          </h2>
        </div>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
        {testimonials.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.05}>
            <article
              data-testid={`testimonial-${i}`}
              className="h-full bg-black border border-[#F89C03]/15 rounded-sm relative overflow-hidden group hover:border-[#F89C03]/40 transition-colors"
            >
              <div className="flex items-center gap-4 p-6 border-b border-[#F89C03]/10 bg-gradient-to-r from-[#F89C03]/5 to-transparent">
                <div>
                  <p className="font-serif-display text-xl text-[#FFB933]">
                    {t.name}
                  </p>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#9B9B9B] mt-1">
                    {t.meta}
                  </p>
                </div>
                <span className="ml-auto font-serif-display text-5xl text-[#F89C03]/30 leading-none select-none">
                  &ldquo;
                </span>
              </div>
              <div className="p-8 whitespace-pre-line text-[#CFCFCF] leading-[1.85] font-light text-[15px]">
                {t.body}
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-6 lg:mt-8">
        {videoTestimonials.map((v, i) => (
          <Reveal key={v.id} delay={i * 0.04}>
            <article
              data-testid={`video-testimonial-${i}`}
              className="h-full bg-black border border-[#F89C03]/15 rounded-sm overflow-hidden group hover:border-[#F89C03]/40 transition-colors"
            >
              <div className="relative w-full aspect-video bg-[#0a0a0a]">
                <iframe
                  src={`https://fast.wistia.net/embed/iframe/${v.id}`}
                  title={v.caption}
                  allow="autoplay; fullscreen"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <p className="font-serif-display italic text-lg text-white leading-snug">
                  &ldquo;{v.caption}&rdquo;
                </p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <p className="mt-16 text-center font-serif-display italic text-xl sm:text-2xl text-[#FFB933] max-w-3xl mx-auto leading-snug">
          I have More than 45,000+ Testimonial Videos, But these are sufficient
          for now...
        </p>
      </Reveal>
    </div>
  </section>
);

/* ============================ GUARANTEE ============================ */
export const Guarantee = ({ onCta }) => (
  <section data-testid="section-guarantee" className="py-24 sm:py-32 relative">
    <div className="max-w-4xl mx-auto px-6 lg:px-8">
      <Reveal>
        <div className="border border-[#F89C03]/40 p-10 sm:p-16 bg-gradient-to-b from-[#0a0a0a] to-black rounded-sm relative">
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-black px-6 py-2 border border-[#F89C03]/40">
            <div className="flex items-center gap-2 text-[#F89C03] tracking-[0.35em] uppercase text-xs">
              <ShieldCheck className="w-4 h-4" />
              The Guarantee
            </div>
          </div>

          <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight mt-4">
            Two things can happen after you enroll.
          </h2>

          <div className="mt-10 space-y-8">
            <div>
              <p className="text-[#FFB933] font-serif-display italic text-xl mb-3">
                The first thing:
              </p>
              <Paragraph>
                You start. You go through Module 2. You feel the state — and you
                will know when you are in it, because it feels like nothing
                else. You start working on the thing you most want to change.
                Something shifts. You continue. By the time you reach Module 4
                you are using this on yourself and on someone in your family,
                and you are watching it work at a level that nothing else ever
                reached.
              </Paragraph>
              <Paragraph className="mt-4 text-[#9B9B9B] italic">
                This is what happens for most people.
              </Paragraph>
            </div>

            <div className="gold-divider" />

            <div>
              <p className="text-[#FFB933] font-serif-display italic text-xl mb-3">
                The second thing:
              </p>
              <Paragraph>
                You go through the modules, you follow the process, and
                something does not connect for you. Within 60 days, you send one
                message. No long explanation needed. No complicated process.
                Every rupee comes back to you.
              </Paragraph>
            </div>

            <div className="gold-divider" />

            <div className="space-y-3">
              <Paragraph>
                In the first case: you have a skill you will use every day for
                the rest of your life.
              </Paragraph>
              <Paragraph>
                In the second case: you learned more about your own mind than
                most people learn in a lifetime, and it cost you nothing.
              </Paragraph>
              <p className="font-serif-display italic text-2xl text-white pt-2">
                There is no bad outcome here.
              </p>
            </div>
          </div>

          <div className="mt-12">
            <Button
              data-testid="cta-guarantee"
              onClick={onCta}
              className="h-13 px-8 py-4 bg-[#F89C03] hover:bg-[#FFB933] text-black font-semibold tracking-wide rounded-full cta-shine"
            >
              Enroll — ₹3,500 with 60-Day Guarantee
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ============================ PRICING ============================ */
export const Pricing = ({ onCta }) => (
  <section
    data-testid="section-pricing"
    className="py-24 sm:py-32 bg-[#060606] relative overflow-hidden"
  >
    <div className="max-w-5xl mx-auto px-6 lg:px-8 relative">
      <Reveal>
        <div className="text-center mb-16">
          <SectionLabel>The Price</SectionLabel>
          <div className="font-serif-display text-7xl sm:text-8xl lg:text-9xl text-white leading-none tracking-tight">
            ₹<span className="shimmer-text">3,500</span>
          </div>
        </div>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        <Reveal delay={0.05}>
          <div className="p-8 border border-[#F89C03]/15 bg-black/60 rounded-sm">
            <p className="text-xs tracking-[0.3em] uppercase text-[#9B9B9B] mb-4">
              One Hypnotherapist Session
            </p>
            <p className="font-serif-display text-4xl text-[#9B9B9B] mb-4">
              ₹2,000 – ₹5,000
            </p>
            <Paragraph className="text-sm">
              One session in any Indian city — Mumbai, Delhi, Bengaluru,
              Hyderabad. One hour. You go home, you wait and see, you book the
              next one.
            </Paragraph>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="p-8 border-2 border-[#F89C03] bg-black rounded-sm relative glow-pulse">
            <div className="absolute -top-3 left-6 bg-[#F89C03] text-black text-xs tracking-[0.25em] uppercase px-3 py-1 font-semibold">
              This Course
            </div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#F89C03] mb-4 mt-2">
              The Skill Itself
            </p>
            <p className="font-serif-display text-4xl text-[#FFB933] mb-4">
              ₹3,500 · once
            </p>
            <Paragraph className="text-sm">
              The skill of doing it yourself — forever. For yourself. For your
              family. Anytime. Without booking anyone. Without travelling
              anywhere. Without paying again.
            </Paragraph>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div className="text-center mt-16 space-y-3">
          <p className="font-serif-display italic text-2xl sm:text-3xl text-white">
            ₹3,500 for a skill you own permanently.
          </p>
          <Paragraph className="text-[#9B9B9B]">
            That is less than ₹10 a day for a year.
          </Paragraph>
          <Paragraph className="text-[#9B9B9B]">
            Less than a plate of food at a decent restaurant.
          </Paragraph>
          <p className="font-serif-display text-xl text-[#FFB933] pt-4">
            For the tool that goes where everything else cannot.
          </p>
        </div>

        <div className="flex justify-center mt-12">
          <Button
            data-testid="cta-pricing"
            onClick={onCta}
            className="h-14 px-10 bg-[#F89C03] hover:bg-[#FFB933] text-black font-semibold tracking-wide rounded-full text-base cta-shine glow-pulse"
          >
            Yes, I Want to Learn Hypnosis — ₹3,500
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ============================ CLOSE ============================ */
export const CloseSection = ({ onCta }) => (
  <section data-testid="section-close" className="relative overflow-hidden">
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-gradient-to-b from-[#150c04] via-black to-black" />
      <div className="absolute inset-0 candle-glow" />
    </div>

    <div className="relative max-w-3xl mx-auto px-6 lg:px-8 py-24 sm:py-32">
      <Reveal>
        <div className="space-y-7">
          <Paragraph>
            You have been curious about hypnosis for a long time.
          </Paragraph>
          <Paragraph>
            That curiosity was not random. It was not just interest in something
            unusual.
          </Paragraph>
          <Paragraph>
            Something in you — deeper than the thinking mind, deeper than the
            part that makes lists and sets reminders and tries harder — already
            knew this was real. Already knew this was the level that needed to
            be reached.
          </Paragraph>
          <p className="font-serif-display italic text-2xl sm:text-3xl text-[#FFB933]">
            You were right.
          </p>
          <Paragraph>
            Most people who feel this pull never step in. They watch videos.
            They read about it. They stay fascinated but at a distance. The
            pattern they wanted to change is still running ten years later.
          </Paragraph>
          <Paragraph className="text-white">
            You have found the way in.
          </Paragraph>
          <Paragraph className="text-white">It is right here.</Paragraph>
          <Paragraph className="text-white">It has been waiting.</Paragraph>
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <Button
            data-testid="cta-close"
            onClick={onCta}
            className="h-14 px-8 bg-[#F89C03] hover:bg-[#FFB933] text-black font-semibold tracking-wide rounded-full text-base glow-pulse cta-shine"
          >
            Yes, I Want to Learn Hypnosis — ₹3,500 — 60-Day Guarantee
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ============================ P.S. ============================ */
export const PSSection = ({ onCta }) => (
  <section
    data-testid="section-ps"
    className="py-24 sm:py-32 bg-[#060606] relative"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5">
        <Reveal>
          <ParallaxImage
            src={IMG.father_daughter}
            alt="A quiet moment between two people"
            height="h-[520px]"
            overlay="from-black/65 to-transparent"
          />
        </Reveal>
      </div>

      <div className="lg:col-span-7">
        <Reveal>
          <p className="font-serif-display text-5xl sm:text-6xl text-[#FFB933] mb-8">
            P.S.
          </p>
          <Paragraph className="text-lg sm:text-xl text-white">
            Think of the one person in your life who is suffering the most right
            now. The one whose pain you have watched for years and felt helpless
            about. After Module 4 of this course, you will know how to actually
            help them — not with advice, not with sympathy, but with direct
            access to the level where their pain is stored and running. For most
            people, this one application alone is worth ten times the price of
            the course.
          </Paragraph>

          <div className="mt-10">
            <Button
              data-testid="cta-ps"
              onClick={onCta}
              className="h-13 px-8 py-4 bg-transparent border border-[#F89C03] hover:bg-[#F89C03] text-[#FFB933] hover:text-black font-semibold tracking-wide rounded-full transition-all"
            >
              Begin the Course — ₹3,500
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ============================ FAQ ============================ */
const faqs = [
  {
    q: "How can I join the course?",
    a: "You can join the course simply by signing up with your login details and successful payment.",
  },
  {
    q: "How can I watch the videos?",
    a: "After successful payment, you will be sent a WhatsApp message and an email with all the details.",
  },
  {
    q: "Does the videos have any expiry time restriction?",
    a: "No. Once enrolled the videos can be viewed for a lifetime.",
  },
  {
    q: "Can I apply the method shown in this course?",
    a: "You can easily apply the methods shown in the course, if you follow all the steps properly shown in the course.",
  },
];

export const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section data-testid="section-faq" className="py-24 sm:py-32 relative">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <Reveal>
          <h2 className="text-center font-serif-display text-4xl sm:text-5xl text-white leading-tight mb-16">
            FAQ
          </h2>
        </Reveal>

        <div className="space-y-0">
          {faqs.map((f, i) => {
            const open = openIdx === i;
            return (
              <Reveal key={f.q} delay={i * 0.04}>
                <div className="border-b border-[#F89C03]/15 py-7">
                  <button
                    type="button"
                    data-testid={`faq-toggle-${i}`}
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    className="w-full flex items-start justify-between gap-6 text-left"
                  >
                    <h3 className="font-serif-display text-xl sm:text-2xl text-white leading-snug">
                      {f.q}
                    </h3>
                    <span
                      className={`shrink-0 mt-1 w-6 h-6 flex items-center justify-center rounded-full border border-[#F89C03]/40 text-[#F89C03] transition-transform ${
                        open ? "rotate-45" : ""
                      }`}
                    >
                      +
                    </span>
                  </button>
                  {open && (
                    <p className="mt-4 text-[#CFCFCF] leading-[1.85] font-light text-[15px] pr-10">
                      {f.a}
                    </p>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
export const Footer = () => (
  <footer
    data-testid="footer"
    className="pt-16 pb-28 border-t border-[#F89C03]/15 bg-black"
  >
    <div className="max-w-5xl mx-auto px-6 lg:px-8">
      <p className="text-xs sm:text-sm text-[#6B6B6B] leading-relaxed text-center max-w-3xl mx-auto">
        Your privacy on the Internet is of the utmost importance to us. We
        will not sell, distribute or share your information. This site is
        not a part of the Facebook website or Facebook Inc. Additionally,
        this site is NOT endorsed by Facebook in any way. FACEBOOK is a
        trademark of FACEBOOK, Inc. We don't claim to teach you any
        supernatural art. It's a process of using various forms of tricks to
        perform the art.
      </p>

      <div className="mt-12 flex flex-col md:flex-row items-center md:items-center gap-6 md:gap-12 justify-start">
        <div className="flex items-center gap-3 font-serif-display text-xl text-white shrink-0">
          <img
            src="/images/logo.png"
            alt="Hypno Guru"
            className="w-8 h-8 rounded-full object-cover border border-[#F89C03]/40"
          />
          Hypnosis — The Course
        </div>

        <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-6 gap-y-2 text-xs tracking-[0.15em] uppercase text-[#9B9B9B]">
          <a
            href="https://mindreading.learnhypnoguru.com/legal/contact-us.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#F89C03] transition-colors"
          >
            Contact Us
          </a>
          <a
            href="https://mindreading.learnhypnoguru.com/legal/privacy-policy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#F89C03] transition-colors"
          >
            Privacy Policy
          </a>
          <a
            href="https://mindreading.learnhypnoguru.com/legal/terms-of-service.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#F89C03] transition-colors"
          >
            Terms of Service
          </a>
          <a
            href="https://mindreading.learnhypnoguru.com/legal/refund-policy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#F89C03] transition-colors"
          >
            Refund Policy
          </a>
        </div>
      </div>

      <p className="mt-8 text-xs tracking-[0.2em] uppercase text-[#5B5B5B] text-center md:text-left">
        © 26 by Hypno Mesmerism School Of India. All rights reserved.
      </p>
    </div>
  </footer>
);
